// bucketTransforms.js

// Utilities
import { isAiConversation, isConversationChannel, isVoiceVideoChannel } from 'channels/constants/channelTypes';
import { AI_CONVERSATION_KIND, AI_TITLE } from 'spaces/constants/aiConstants';
import { buildAiChannelKey } from 'spaces/utils/aiChannelKeys';
import { sortConversationsByActivity } from 'channels/utils/channelFilters';
import { buildConversationDisplayTitle } from 'channels/utils/conversationDisplayTitle';
import { LIVE_ROOMS_SURFACE_ENABLED } from 'features/liveRoomsSurface';
import {
  buildQualifiedDatabaseDisplayLabel,
  dedupeAppInstanceEntries,
  getAppDisplayTitleFromTypeDef,
  isAppProvisionedType,
  pickPreferredAppInstanceEntry,
  resolveAppInstanceGroupKey,
} from 'utilities/baseDatabaseDisplay';
import { getAppInstanceDisplayTitle, getAppInstanceGroupKey } from 'services/appInstances/appInstanceModel';
import { isSystemTypeData } from 'security/typeAccess';
import { typeShowsInMenu } from 'utilities/typeDisplayPolicy';
import { getMenuAppearAsRows, typeHasMenuAppearAs } from 'utilities/typeAppearPolicy';

// Shared Value Normalizer
export const normalizeBucketValue = (value) => String(value || '').trim();

/** System media types shown under the sidebar MEDIA section. */
export const SIDEBAR_MENU_MEDIA_TYPE_KEYS = Object.freeze([
  'PHOTO',
  'VIDEO',
  'DOCUMENT',
  'FILE',
]);

const SIDEBAR_MENU_MEDIA_TYPE_SET = new Set(SIDEBAR_MENU_MEDIA_TYPE_KEYS);

function resolveSidebarDatabaseTypeKey(row) {
  return normalizeBucketValue(row?.database?.type || row?.type).toUpperCase();
}

/**
 * Split library sidebar rows into MENU, MEDIA, and COLLECTIONS.
 * MEDIA types are ordered PHOTO → VIDEO → DOCUMENT → FILE.
 *
 * @param {Array<object>} rows - Rows from buildBaseDatabases.
 * @param {function} getType - Node type lookup.
 * @returns {{ menuPrimary: object[], menuMedia: object[], customCollections: object[] }}
 */
/**
 * @param {object} row
 * @param {object|null} typeDef
 * @returns {object[]}
 */
function expandMenuAppearAsRows(row, typeDef) {
  return getMenuAppearAsRows(typeDef).map((appear) => ({
    ...row,
    key: `${row.databaseKey || row.key}::appearAs:${appear.key}`,
    appearAsKey: appear.key,
    iconKey: appear.iconKey || row.iconKey,
    label: appear.labelPlural || appear.label,
  }));
}

export function partitionLibrarySidebarDatabases(rows = [], getType) {
  const menuPrimary = [];
  const menuMedia = [];
  const customCollections = [];
  const mediaByKey = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row?.key) return;
    const typeKey = resolveSidebarDatabaseTypeKey(row);
    const typeDef = typeKey && typeof getType === 'function' ? getType(typeKey) : null;
    const isSystem = isSystemTypeData(typeDef);
    const showTypeInMenu = typeShowsInMenu(typeDef || typeKey);
    const appearRows = expandMenuAppearAsRows(row, typeDef);

    if (showTypeInMenu || typeHasMenuAppearAs(typeDef)) {
      if (SIDEBAR_MENU_MEDIA_TYPE_SET.has(typeKey)) {
        mediaByKey.set(typeKey, row);
        return;
      }
      if (showTypeInMenu) menuPrimary.push(row);
      appearRows.forEach((appearRow) => menuPrimary.push(appearRow));
      return;
    }

    if (!isSystem) {
      customCollections.push(row);
    }
  });

  SIDEBAR_MENU_MEDIA_TYPE_KEYS.forEach((typeKey) => {
    const row = mediaByKey.get(typeKey);
    if (row) menuMedia.push(row);
  });

  return {
    menuPrimary,
    menuMedia,
    customCollections,
  };
}

const canViewerSeeDashboard = ({ dashboard, viewerUserKey }) => {
    const isPrivate = dashboard?.private === true;
    if (!isPrivate) return true;

    const creatorKey = normalizeBucketValue(dashboard?.createdBy);
    if (!creatorKey) return true;

    return creatorKey === normalizeBucketValue(viewerUserKey);
};

function buildDatabaseSidebarRow({
  database,
  def,
  language,
  nestedUnderApp = false,
  key: rowKey = null,
  iconResource: iconResourceOverride,
  label: labelOverride,
}) {
    const databaseKey = normalizeBucketValue(database?.key);
    // Native connector-owned types use the connector logo.
    // Platform catch-alls use the type icon (merged organic + sourced).
    const iconResource = iconResourceOverride !== undefined
      ? iconResourceOverride
      : undefined;

    return {
        key: normalizeBucketValue(rowKey) || databaseKey,
        databaseKey,
        iconKey: (typeof def?.iconKey === 'string' ? def.iconKey.trim() : '') || 'deployed_code',
        iconResource,
        label: labelOverride || buildQualifiedDatabaseDisplayLabel({
            database,
            language,
            nestedUnderApp,
            typeDef: def,
        }),
        database,
    };
}

// Base Database Rows
export const buildBaseDatabases = ({
  databases,
  getType,
  language,
  baseKey,
}) => {
    const rows = [];

    (Array.isArray(databases) ? databases : [])
        .filter((database) => {
            const typeKey = normalizeBucketValue(database?.type);
            if (!typeKey) return true;

            const def = getType(typeKey);
            if (!def) return !isAppProvisionedType(null, database);
            // App-provisioned types render under Apps; MENU/COLLECTIONS partition later.
            return !isAppProvisionedType(def, database);
        })
        .forEach((database) => {
            const typeKey = database?.type || '';
            const def = typeKey ? getType(typeKey) : null;
            const row = buildDatabaseSidebarRow({ database, def, language });
            if (row.key) rows.push(row);
        });

    return rows
      .filter((row) => row.key)
      .sort((a, b) => a.label.localeCompare(b.label) || String(a.key).localeCompare(String(b.key)));
};

// Base Installed App Rows
export const buildBaseInstalledApps = ({
  appInstances = [],
  databases,
  getType,
  language,
  baseKey,
}) => {
  const cleanedAppInstances = Array.isArray(appInstances) ? appInstances : [];

  if (cleanedAppInstances.length > 0) {
    const databaseByTypeKey = new Map();

    (Array.isArray(databases) ? databases : []).forEach((database) => {
      const typeKey = normalizeBucketValue(database?.type);
      if (!typeKey) return;
      databaseByTypeKey.set(typeKey, database);
    });

    return cleanedAppInstances
      .map((appInstance) => {
        const groupKey = getAppInstanceGroupKey(appInstance?.key);
        const entries = (Array.isArray(appInstance?.databases) ? appInstance.databases : [])
          .map((databaseRef) => {
            const typeKey = normalizeBucketValue(databaseRef?.typeKey);
            if (!typeKey) return null;

            const database = databaseByTypeKey.get(typeKey);
            const def = getType(typeKey);
            if (!database && !def) return null;

            return {
              database: database || {
                key: databaseRef?.collectionKey || databaseRef?.databaseKey,
                title: databaseRef?.labelPlural || databaseRef?.label,
                type: typeKey,
              },
              def,
            };
          })
          .filter(Boolean);

        const dedupedEntries = dedupeAppInstanceEntries(entries);

        return {
          key: groupKey || normalizeBucketValue(appInstance?.key),
          title: getAppInstanceDisplayTitle(appInstance),
          databases: dedupedEntries
            .map(({ database, def }) => buildDatabaseSidebarRow({
              database,
              def,
              language,
              nestedUnderApp: true,
            }))
            .filter((row) => row.key)
            .sort((left, right) => left.label.localeCompare(right.label)),
        };
      })
      .filter((group) => group.databases.length > 0)
      .sort((left, right) => left.title.localeCompare(right.title));
  }

  const appGroups = new Map();

    (Array.isArray(databases) ? databases : []).forEach((database) => {
        const typeKey = normalizeBucketValue(database?.type);
        if (!typeKey) return;

        const def = getType(typeKey);
        if (!isAppProvisionedType(def, database)) return;

        const typeBaseKey = normalizeBucketValue(def?.baseKey || database?.baseKey);
        const activeBaseKey = normalizeBucketValue(baseKey);
        if (typeBaseKey && activeBaseKey && typeBaseKey !== activeBaseKey) return;

        const groupKey = resolveAppInstanceGroupKey(def, database);
        if (!groupKey) return;

        const existing = appGroups.get(groupKey) || {
            entries: [],
            key: groupKey,
        };

        existing.entries.push({ database, def });
        appGroups.set(groupKey, existing);
    });

    return Array.from(appGroups.values())
        .map((group) => {
            const entries = dedupeAppInstanceEntries(group.entries);
            const preferredEntry = pickPreferredAppInstanceEntry(entries);
            const appTitle = getAppDisplayTitleFromTypeDef(preferredEntry?.def, preferredEntry?.database) || 'Installed App';

            return {
                key: group.key,
                title: appTitle,
                databases: entries
                    .map(({ database, def }) => buildDatabaseSidebarRow({
                        database,
                        def,
                        language,
                        nestedUnderApp: true,
                    }))
                    .filter((row) => row.key)
                    .sort((left, right) => left.label.localeCompare(right.label)),
            };
        })
        .filter((group) => group.databases.length > 0)
        .sort((left, right) => left.title.localeCompare(right.title));
};

// Base Dashboard Rows
export const buildBaseDashboards = ({ dashboards, viewerUserKey, pinnedDashboardKey }) => {
    const normalizedPinnedDashboardKey = normalizeBucketValue(pinnedDashboardKey);

    return (Array.isArray(dashboards) ? dashboards : [])
        .filter((row) => canViewerSeeDashboard({ dashboard: row, viewerUserKey }))
        .map((row) => {
            const key = normalizeBucketValue(row?.key);

            return {
                key,
                title: row?.title || 'Untitled Dashboard',
                iconKey: typeof row?.iconKey === 'string' && row.iconKey.trim() ? row.iconKey.trim() : 'dashboard',
                private: row?.private === true,
                pinned: Boolean(normalizedPinnedDashboardKey && key === normalizedPinnedDashboardKey),
            };
        })
        .filter((row) => row.key)
        .sort((a, b) => a.title.localeCompare(b.title));
};

// Base Channel Rows
export const buildBaseChannels = ({ channels }) => {
    return (Array.isArray(channels) ? channels : [])
        .filter((row) => !isConversationChannel(row?.channelType))
        // Orphaned legacy Shared Space fixture channels — keep out of the global list.
        .filter((row) => !String(row?.sharedSpaceKey || '').trim())
        // Hide LiveKit voice/video topic channels while the surface is gated off.
        .filter((row) => LIVE_ROOMS_SURFACE_ENABLED || !isVoiceVideoChannel(row?.channelType))
        .map((row) => ({
            key: normalizeBucketValue(row?.key),
            title: row?.title || 'Untitled Channel',
            iconKey: isVoiceVideoChannel(row?.channelType) ? 'video_camera_front' : 'tag',
        }))
        .filter((row) => row.key)
        .sort((a, b) => a.title.localeCompare(b.title));
};

// Base Conversation Rows
export const buildBaseAiConversation = ({
    channels,
    currentUserKey,
    baseKey,
    unreadByChannel = {},
}) => {
    const cleanUserKey = normalizeBucketValue(currentUserKey);
    const cleanBaseKey = normalizeBucketValue(baseKey);
    if (!cleanUserKey || !cleanBaseKey) return null;

    const key = buildAiChannelKey(cleanBaseKey, cleanUserKey);
    if (!key) return null;

    const row = (Array.isArray(channels) ? channels : []).find((channel) => (
        normalizeBucketValue(channel?.key) === key
        && isAiConversation(channel)
    ));

    return {
        key: normalizeBucketValue(row?.key) || key,
        title: AI_TITLE,
        iconKey: 'wand_stars',
        channel: row || {
            key,
            channelType: 'CONVERSATION',
            conversationKind: AI_CONVERSATION_KIND,
            memberKeys: [cleanUserKey],
            title: AI_TITLE,
            messagingEnabled: true,
        },
        unread: unreadByChannel[key] === true,
        pinned: true,
    };
};

export const buildBaseConversations = ({
    channels,
    currentUserKey,
    membersByKey = {},
    unreadByChannel = {},
}) => {
    const cleanUserKey = normalizeBucketValue(currentUserKey);

    return sortConversationsByActivity(
        (Array.isArray(channels) ? channels : []).filter((row) => (
            isConversationChannel(row?.channelType)
            && !isAiConversation(row)
        ))
    )
        .map((row) => {
            const key = normalizeBucketValue(row?.key);
            if (!key) return null;

            return {
                key,
                title: buildConversationDisplayTitle({
                    currentUserKey: cleanUserKey,
                    memberKeys: row?.memberKeys,
                    membersByKey,
                }),
                iconKey: 'chat',
                channel: row,
                unread: unreadByChannel[key] === true,
            };
        })
        .filter(Boolean);
};

// Composed Buckets
export const composeBuckets = ({
    baseDatabases,
    baseInstalledApps,
    baseDashboards,
    baseChannels,
    baseConversations,
    baseAppInstances = [],
}) => {
    return [
        {
            key: 'base',
            title: 'Space',
            conversations: baseConversations,
            channels: baseChannels,
            databases: baseDatabases,
            installedApps: baseInstalledApps,
            dashboards: baseDashboards,
            appInstances: Array.isArray(baseAppInstances) ? baseAppInstances : [],
        },
    ];
};
